/** The 3-2-1 before a rapid-fire round. The deadline is requested when this ENDS — ADR-0004 §2. */
import React, { useEffect, useRef, useState } from 'react';

export const RAPID_FIRE_BEAT_STEP_MS = 750;
const BEAT_FROM = 3;

interface RapidFireBeatProps {
  label: string;
  onDone: () => void;
}

const RapidFireBeat: React.FC<RapidFireBeatProps> = ({ label, onDone }) => {
  const [count, setCount] = useState(BEAT_FROM);
  // Reduced motion drops the animation, never the beat itself (ADR-0004 §2).
  const reduced =
    typeof window !== 'undefined' &&
    Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);

  const firedRef = useRef(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const id = setInterval(() => setCount((c) => Math.max(0, c - 1)), RAPID_FIRE_BEAT_STEP_MS);
    intervalRef.current = id;
    return () => clearInterval(id);
  }, []);

  // Never from inside the updater, and the guard is the ref rather than the count — ADR-0004 §2.
  useEffect(() => {
    if (count > 0 || firedRef.current) return;
    firedRef.current = true;
    if (intervalRef.current !== null) clearInterval(intervalRef.current);
    onDone();
  }, [count, onDone]);

  return (
    <div className="text-center px-8 py-16 min-h-[360px] flex flex-col items-center justify-center gap-4">
      <span className="text-xs font-bold uppercase tracking-widest text-blue-900">{label}</span>
      <span
        role="status"
        className={`text-7xl font-bold text-blue-600 tabular-nums ${reduced ? '' : 'animate-ping'}`}
      >
        {count > 0 ? count : 'Go'}
      </span>
      <p className="text-sm text-gray-500">Get comfortable. The clock has not started.</p>
    </div>
  );
};

export default RapidFireBeat;
